import React, { FunctionComponent } from 'react';
import Translate from 'ui/atoms/translate';
import BoxedContent from 'ui/molecules/boxed-content';
import { extractNaturalAndLegalFromPerson } from 'core/api/conversions';
import Button from 'ui/atoms/button';
import ActionButtons from 'ui/molecules/action-buttons';
import {
  NaturalPerson,
  LegalPerson,
  Identification,
  AdminInvestor,
  BenefitingPerson,
  KycStatusEnum,
  InvestorEntryType,
} from 'api';
import { PersonType } from 'ui/types/person';
import useEditInvestorData from 'hooks/use-edit-investor-data';
import IssuerInvestorEditModals from 'apps/issuer/pages/investor-details/edit-modals';
import useUserData from './use-user-data';
import IssuerUserInformation from './user-information';
import WalletsInformation from './wallets-information';
import { useCurrentUserSelector } from 'src/core/auth/hooks';
import { hasDistributionManageDataPermission } from 'src/apps/issuer/helpers/distributionPlatformPermissions';

interface UserDataViewProps {
  investor: AdminInvestor;
  benefitingPersons?: BenefitingPerson[];
  canManage: boolean;
  fetchInvestorDetails: () => void;
  restartIdentification: () => void;
  identification?: Identification;
  canAddIdentification: boolean;
}

const UserDataView: FunctionComponent<UserDataViewProps> = (props) => {
  const {
    investor,
    canManage,
    fetchInvestorDetails,
    identification,
    benefitingPersons,
    restartIdentification,
    canAddIdentification,
  } = props;

  const investorDataProps = useEditInvestorData();

  const { naturalPerson, legalPerson } = extractNaturalAndLegalFromPerson(investor?.person);

  const rootPerson = investor?.person.personType === PersonType.Legal ? legalPerson : naturalPerson;

  const { currentUser } = useCurrentUserSelector();

  const canManageData = hasDistributionManageDataPermission(
    currentUser?.associatedDistributionPlatforms?.filter(
      (platform) => platform.id === investor?.distributionPlatformId,
    ) || null,
  );

  const userSections = useUserData({
    ...investorDataProps,
    investor,
    rootPerson,
    naturalPerson,
    legalPerson,
    identification,
    benefitingPersons: benefitingPersons,
    canReidentify: investor.canReidentify && canManageData,
    canManage,
    canAddIdentification,
    restartIdentification: restartIdentification,
  });

  return (
    <>
      {userSections.map((userDataSet) => {
        if (!userDataSet) {
          return null;
        }
        const editButtons = userDataSet.editButtons;

        if (userDataSet.key === 'walletsData') {
          return (
            <WalletsInformation
              investorId={investor.id}
              isKycSuccess={investor?.kycStatus === KycStatusEnum.SUCCESS}
              canManage={
                canManage &&
                (investor.entryType === InvestorEntryType.CUMULATIVE_ENTRY ||
                  !!rootPerson?.taxInformation?.taxIdentificationNumber)
              }
            />
          );
        }
        return (
          <BoxedContent
            title={<Translate name={`dashboardInvestorSettings.${userDataSet.key}.title`} />}
            key={userDataSet.key}
          >
            <>
              <IssuerUserInformation userDataSet={userDataSet} canAddIdentification={canAddIdentification} />
              <ActionButtons verticalAlign spacingTop="regular">
                {editButtons.map(
                  (editInfo) =>
                    editInfo.display && (
                      <Button variant="secondary" onClick={() => editInfo.onClick()}>
                        <Translate name={editInfo.text} />
                      </Button>
                    ),
                )}
              </ActionButtons>
            </>
          </BoxedContent>
        );
      })}
      <IssuerInvestorEditModals
        id={investor?.id || ''}
        naturalPerson={naturalPerson as NaturalPerson}
        legalPerson={legalPerson as LegalPerson}
        rootPerson={rootPerson as NaturalPerson | LegalPerson}
        fetchInvestorDetails={fetchInvestorDetails}
        benefitingOwners={benefitingPersons}
        target2Bic={investor?.target2Bic}
        {...investorDataProps}
      />
    </>
  );
};

export default UserDataView;
